// Config for the guided "Build with AI" course funnel at /build-india (later
// /build-us, /build-mideast — one page component, new region = new entry here,
// zero new code). Edit prices/links/copy per region; used by BuildCoursePage.tsx
// and BuildCourseThanksPage.tsx.
//
// Registrations reuse the webinar plumbing: POST /api/webinar-register with
// webinar_slug = region slug below and segment = tier key, so orders land in
// webinar_registrations with payment_status pending/paid. No backend changes.

export type BuildRegionKey = 'india';
export type BuildTierKey = 'professional' | 'advance';

export interface BuildTier {
  key: BuildTierKey;
  name: string;
  tagline: string;
  price: number;          // numeric, used for the Meta Purchase event value
  priceDisplay: string;
  projects: number;
  highlight: boolean;     // true = "Most popular" card treatment
  // Razorpay Payment PAGE for this tier (Pages, not Links — Links are
  // single-use and have no redirect; the Pages redirect to /thanks, see
  // issue #113 re dropped payment ids). Empty string = TEST MODE: the form
  // skips payment, goes straight to /thanks, and the thanks page does NOT
  // fire the Purchase events. DO NOT launch ads while either link is empty.
  paymentLink: string;
  features: string[];
}

export interface BuildRegionConfig {
  slug: string;           // webinar_registrations.webinar_slug for this funnel
  path: string;           // route base, e.g. '/build-india'
  currency: string;       // ISO code for the Purchase pixel event
  // 'evergreen' = "join anytime — start building the same day". No course
  // duration is shown anywhere on the page (Francis discusses it on enquiry).
  // Flip to 'cohort' + set cohortStartDate to show a fixed batch date on the
  // SAME page — no other code changes.
  mode: 'evergreen' | 'cohort';
  cohortStartDate: string | null;   // display string, e.g. 'Monday, Sep 15'
  // Google Ads conversion label (send_to). Empty = not fired (Meta-only launch).
  googleAdsConversion: string;
  // WhatsApp group invite shown on the thanks page. Empty = email-only fallback.
  whatsappLink: string;
  // WhatsApp number for pre-sale questions, digits only with country code
  // (e.g. '919876543210'). Shown as a floating chat bubble + "questions?"
  // links on the sales page. Empty = all WhatsApp contact UI hidden.
  supportWhatsappNumber: string;
  sampleProjects: Array<{ icon: string; name: string; blurb: string }>;
  tiers: Record<BuildTierKey, BuildTier>;
}

export const BUILD_REGIONS: Record<BuildRegionKey, BuildRegionConfig> = {
  india: {
    slug: 'build-india',
    path: '/build-india',
    currency: 'INR',
    mode: 'evergreen',
    cohortStartDate: null,
    googleAdsConversion: '',
    whatsappLink: '',
    supportWhatsappNumber: '919361110700',

    sampleProjects: [
      { icon: '🤖', name: 'AI Research Agent', blurb: 'Give it a topic. It plans the search, reads sources, and writes a cited report — a real agent, not a chatbot.' },
      { icon: '📄', name: 'Document Intelligence (RAG)', blurb: 'Ask questions across a stack of PDFs and get answers with verifiable citations — the RAG skill every AI job posting lists.' },
      { icon: '💬', name: 'WhatsApp Support Bot', blurb: 'A support bot trained on your own knowledge base, answering where your customers actually chat.' },
      { icon: '🧑‍💻', name: 'Multi-Agent Dev Team', blurb: 'A planner, a coder, and a reviewer — three AI agents building a feature together while you steer and judge the output.' },
      { icon: '📈', name: 'Market Signal Engine', blurb: 'Streaming market data, anomaly detection, and an LLM that explains every signal in plain English.' },
      { icon: '🚀', name: 'Your AI Micro-SaaS', blurb: 'The capstone: pick a niche and ship a working AI product at a real URL recruiters can open.' },
    ],
    tiers: {
      professional: {
        key: 'professional',
        name: 'Professional',
        tagline: 'Everything you need to finish',
        price: 3999,
        priceDisplay: '₹3,999',
        projects: 10,
        highlight: false,
        paymentLink: 'https://rzp.io/rzp/awXgh0N7',
        features: [
          '10 guided AI projects',
          '2 personal 1-on-1 mentor sessions',
          'Doubts answered within 24 hours',
          '3 months of Cubemate Ultimate included',
          'Every project runs live in your browser — no setup',
        ],
      },
      advance: {
        key: 'advance',
        name: 'Advance',
        tagline: "The complete builder's library",
        price: 9999,
        priceDisplay: '₹9,999',
        projects: 30,
        highlight: true,
        paymentLink: 'https://rzp.io/rzp/tpB7PV6',
        features: [
          'All 30 AI projects — the complete library',
          '5 personal 1-on-1 mentor sessions',
          'Doubts answered in under 2 hours (office hours, else next day)',
          '1 year of Cubemate Ultimate included — worth ₹10,188 on its own',
          'Every project runs live in your browser — no setup',
        ],
      },
    },
  },
};
